import * as React from "react"
import * as ProgressPrimitive from "@radix-ui/react-progress"
import { motion } from "framer-motion"
import { cn } from "../../lib/utils"

const Progress = React.forwardRef(({ className, value, animationDuration = 1.5, ...props }, ref) => {
  const [animatedValue, setAnimatedValue] = React.useState(0);
  const isFirstRender = React.useRef(true);

  React.useEffect(() => {
    // 首次渲染时直接设置值
    if (isFirstRender.current) {
      setAnimatedValue(value || 0);
      isFirstRender.current = false;
      return;
    }

    // 后续更新时使用动画
    const startValue = animatedValue;
    const endValue = value || 0;
    const difference = endValue - startValue;
    const startTime = Date.now();

    const animate = () => {
      const now = Date.now();
      const elapsed = (now - startTime) / 1000;
      const progress = Math.min(elapsed / animationDuration, 1);
      
      // 使用easeOutQuart缓动函数
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = startValue + (difference * easeOutQuart);
      
      setAnimatedValue(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setAnimatedValue(endValue);
      }
    };
    
    requestAnimationFrame(animate);
  }, [value, animationDuration, animatedValue]);

  return (
    <ProgressPrimitive.Root
      ref={ref}
      className={cn(
        "relative h-2 w-full overflow-hidden rounded-full bg-secondary",
        className
      )}
      {...props}
    >
      <motion.div
        className="h-full flex-1 bg-gradient-to-r from-blue-500 to-blue-600 dark:from-blue-400 dark:to-blue-500 rounded-full shadow-sm"
        style={{ 
          width: `${animatedValue}%`,
          transformOrigin: "left"
        }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{
          duration: 0.8,
          ease: [0.25, 0.1, 0.25, 1],
          delay: 0.2
        }}
      >
        {/* 添加一个闪光效果 */}
        <motion.div
          className="h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent"
          animate={{
            x: ["-100%", "100%"]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 3,
            ease: "easeInOut"
          }}
        />
      </motion.div>
    </ProgressPrimitive.Root>
  );
});

Progress.displayName = ProgressPrimitive.Root.displayName

export { Progress } 